'use client';

import { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Bell, Search, UserRound } from 'lucide-react';
import {
  fetchPortalCalendar,
  fetchPortalNews,
  resolveActiveCalendarDate,
  type PortalCalendarEvent,
  type PortalNewsItem,
} from '../lib/portal-feed';
import { fetchPortalConfig, formatPortalCollected } from '../lib/portal-config';
import './portal-v3.css';

const asset = (name: string) => `/assets/figma-portal-v3/${name}`;

type GameItem = {
  id: string;
  key?: string;
  name: string;
  image: string;
  banner?: string;
  href?: string;
  collected?: string;
  collectedAt?: string;
};

// Online game records currently expose icons/covers, but not a dedicated wide
// portal banner. Keep the locally designed banner art as a visual-only layer.
const localBannerByGame = {
  bangdream: asset('raw-14.png'),
  rellion: asset('banner-reilian.png'),
  arcanatactics: asset('banner-arcana.png'),
  qunying: asset('banner-heroes.png'),
  sp: asset('banner-star-leap.png'),
  mooncorona: asset('banner-moon-crown.png'),
} satisfies Record<string, string>;

const characters = [
  { name: '露弥', game: '星穹手记', date: '09.12 生日', image: asset('character-lumi.jpeg'), className: 'is-tall' },
  { name: '夜巡', game: '星轨余晖', date: '09.26 生日', image: asset('character-night.jpeg'), className: 'is-tall' },
  { name: '莉西亚', game: '雾海纪行', date: '09.18 生日', image: asset('character-lisia.jpeg'), className: 'is-medium' },
  { name: '维音', game: '花境旅人', date: '09.28 生日', image: asset('character-weiyin.jpeg'), className: 'is-medium' },
  { name: '月见', game: '月隐之境', date: '09.22 生日', image: asset('character-tsukimi.jpeg'), className: 'is-short' },
];

function SectionHeading({ title, subtitle, href = '#' }: { title: string; subtitle?: string; href?: string }) {
  return (
    <div className="pv3-section-heading">
      <div className="pv3-heading-title">
        <h2>{title}</h2>
        {subtitle && <span>{subtitle}</span>}
      </div>
      <a href={href} className="pv3-view-all">
        查看全部 <ArrowRight size={14} aria-hidden="true" />
      </a>
    </div>
  );
}

function SearchBox({ compact = false, value, onChange, onSubmit }: { compact?: boolean; value: string; onChange: (value: string) => void; onSubmit: () => void }) {
  return (
    <form className={`pv3-search ${compact ? 'is-compact' : ''}`} onSubmit={(event) => { event.preventDefault(); onSubmit(); }}>
      <button type="submit" aria-label="搜索"><Search size={compact ? 18 : 22} /></button>
      <input value={value} onChange={(event) => onChange(event.target.value)} placeholder="搜索游戏、角色、攻略" aria-label="搜索游戏、角色、攻略" />
    </form>
  );
}

export default function PortalV3() {
  const [query, setQuery] = useState('');
  const [games, setGames] = useState<GameItem[]>([]);
  const [news, setNews] = useState<PortalNewsItem[]>([]);
  const [calendar, setCalendar] = useState<PortalCalendarEvent[]>([]);
  const [onlineLoading, setOnlineLoading] = useState(true);
  const [selectedGameIndex, setSelectedGameIndex] = useState(0);
  const [isRankingHovered, setIsRankingHovered] = useState(false);
  const [characterTab, setCharacterTab] = useState<'birthday' | 'arrival'>('birthday');

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetchPortalConfig(),
      fetchPortalNews({ page: 1, pageSize: 5 }),
      fetchPortalCalendar({ page: 1, pageSize: 10 }),
    ])
      .then(([portalConfig, newsPage, calendarPage]) => {
        if (cancelled) return;
        const onlineGames = portalConfig.games
          .filter((game) => !game.hidden)
          .map((game) => {
            const visualKey = game.id || game.key || '';
            return {
              id: game.id,
              key: game.key,
              name: game.name,
              image: game.coverUrl || game.icon,
              banner:
                game.banner ||
                localBannerByGame[visualKey as keyof typeof localBannerByGame] ||
                game.coverUrl ||
                game.icon,
              href: `/${game.id}`,
              collected: formatPortalCollected(game.collectedAt),
              collectedAt: game.collectedAt,
            } satisfies GameItem;
          });
        setGames(onlineGames);
        setNews(newsPage.items);
        setCalendar(calendarPage.items);
      })
      .catch(() => {
        // 保持空态，不展示加载失败文案
      })
      .finally(() => {
        if (!cancelled) setOnlineLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const ranking = useMemo(() => games.slice(0, 6), [games]);

  useEffect(() => {
    if (isRankingHovered || ranking.length <= 1) return;
    const timer = window.setInterval(() => {
      setSelectedGameIndex((value) => (value + 1) % ranking.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [isRankingHovered, ranking.length]);

  useEffect(() => {
    if (selectedGameIndex >= ranking.length) setSelectedGameIndex(0);
  }, [ranking.length, selectedGameIndex]);

  const filteredGames = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    if (!keyword) return games;
    return games.filter((game) => game.name.toLowerCase().includes(keyword));
  }, [games, query]);

  const displayedCharacters = useMemo(() => {
    if (characterTab === 'birthday') return characters;
    return [...characters].reverse();
  }, [characterTab]);

  const selectedGame = ranking[selectedGameIndex] || ranking[0];
  const latestGames = useMemo(
    () =>
      [...filteredGames]
        .sort((a, b) => (b.collectedAt || '').localeCompare(a.collectedAt || ''))
        .slice(0, 18),
    [filteredGames],
  );
  const calendarMonthLabel = calendar[0]?.eventDate
    ? calendar[0].eventDate.slice(0, 7).replace('-', ' / ')
    : '近期日程';
  const todayCalendarDate = useMemo(
    () => resolveActiveCalendarDate(calendar),
    [calendar],
  );
  const focusSearch = () => {
    document.querySelector('.pv3-latest')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="portal-v3-page">
      <section className="pv3-hero">
        <div className="pv3-hero-content">
          <p className="pv3-eyebrow">GAME WIKI</p>
          <h1>你的游戏资料库</h1>
          <SearchBox value={query} onChange={setQuery} onSubmit={focusSearch} />
          <div className="pv3-stats" aria-label="平台收录统计">
            <span>收录游戏 <b>{onlineLoading ? '—' : games.length}</b> 款</span>
            <span>已建 Wiki <b>{onlineLoading ? '—' : games.length}</b> 个</span>
            <span>角色图鉴 <b>—</b> 个</span>
          </div>
          <p className="pv3-hero-note">游戏、资讯、日历来自线上数据 · Banner 与角色动态暂无线上字段</p>
        </div>
      </section>

      <nav className="pv3-navigation" aria-label="主导航">
        <div className="pv3-navigation-inner">
          <div className="pv3-nav-links">
            <a className="is-active" href="/">首页</a>
            <a href="/wikis">游戏 Wiki</a>
            <a href="/updates">新游与资讯</a>
            <a href="/characters">角色互动</a>
          </div>
          <SearchBox compact value={query} onChange={setQuery} onSubmit={focusSearch} />
          <div className="pv3-account-controls">
            <button type="button"><Bell size={18} />消息</button>
            <button type="button"><UserRound size={18} />个人中心</button>
          </div>
        </div>
      </nav>

      <main className="pv3-main">
        <section className="pv3-section pv3-wiki-section">
          <SectionHeading title="头部WIKI" href="/wikis" />
          <div className="pv3-featured-layout">
            {selectedGame ? (
              <a href={selectedGame.href || '#'} className="pv3-featured-card">
                <img src={selectedGame.banner} alt={selectedGame.name} />
                <div className="pv3-featured-overlay">
                  <span className="pv3-featured-tag">本周焦点</span>
                  <h3>{selectedGame.name}</h3>
                  <span className="pv3-featured-entry">进入 Wiki <ArrowRight size={17} /></span>
                </div>
              </a>
            ) : (
              <div className="pv3-featured-card pv3-online-empty" aria-hidden="true" />
            )}
            <div className="pv3-dots" aria-label="焦点轮播">
              {ranking.map((game, index) => (
                <button type="button" key={game.id} className={index === selectedGameIndex ? 'is-active' : ''} onClick={() => setSelectedGameIndex(index)} aria-label={`切换到${game.name}`} />
              ))}
            </div>
            <aside className="pv3-ranking" aria-label="热门 Wiki" onMouseEnter={() => setIsRankingHovered(true)} onMouseLeave={() => setIsRankingHovered(false)}>
              {ranking.map((game, index) => (
                <a href={game.href || '#'} key={game.id} className={`pv3-ranking-item ${index === selectedGameIndex ? 'is-selected' : ''}`} onMouseEnter={() => setSelectedGameIndex(index)} onFocus={() => setSelectedGameIndex(index)}>
                  <b className={index < 3 ? 'is-top' : ''}>{String(index + 1).padStart(2, '0')}</b>
                  <img src={game.image} alt="" />
                  <span><strong>{game.name}</strong><small>图鉴 · 攻略 · 社区</small></span>
                  <ArrowRight size={14} />
                </a>
              ))}
            </aside>
          </div>

          <div className="pv3-latest">
          <SectionHeading title="最新wiki" href="/wikis" />
            <div className="pv3-game-grid">
              {latestGames.map((game) => (
                <a className="pv3-game-item" href={game.href || '#'} key={game.id}>
                  <img src={game.image} alt="" />
                  <strong title={game.name}>{game.name}</strong>
                  <small>{game.collected || '\u00a0'}</small>
                </a>
              ))}
            </div>
          </div>
        </section>

        <section className="pv3-section pv3-news-section">
          <SectionHeading title="游戏资讯" href="/updates" />
          <div className="pv3-news-layout">
            <div className="pv3-news-list">
              <h3>最新资讯</h3>
              {news.map((item) => (
                <a href="/updates" className="pv3-news-item" key={item.id}>
                  <img src={item.coverUrl} alt="" />
                  <span><strong>{item.title}</strong><small>{item.gameName}<time>{item.date}</time></small></span>
                </a>
              ))}
            </div>
            <div className="pv3-calendar">
              <div className="pv3-subheading"><h3>游戏日历</h3><span>{calendarMonthLabel}</span></div>
              <div className="pv3-calendar-grid">
                {calendar.map((item) => (
                  <a
                    href="/updates"
                    className={`pv3-calendar-item${item.eventDate === todayCalendarDate ? ' is-today' : ''}`}
                    key={item.id}
                  >
                    <time dateTime={item.eventDate}>{item.eventDate.slice(5).replace('-', '.')}<i /></time>
                    <img src={item.coverUrl} alt="" />
                    <strong>{item.gameName || item.title}</strong>
                    <small>{item.eventType}</small>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="pv3-section pv3-character-section">
          <SectionHeading title="角色动态" subtitle="记录每一次相遇 · 虚构角色展示" href="/characters" />
          <div className="pv3-character-tabs">
            <button className={characterTab === 'birthday' ? 'is-active' : ''} onClick={() => setCharacterTab('birthday')}>最近生日</button>
            <button className={characterTab === 'arrival' ? 'is-active' : ''} onClick={() => setCharacterTab('arrival')}>最近实装</button>
          </div>
          <div className="pv3-character-grid">
            {displayedCharacters.map((item) => (
              <a href="/characters" className={`pv3-character-card ${item.className}`} key={item.name}>
                <img src={item.image} alt={`${item.name}角色插画`} />
                <span className="pv3-character-overlay"><strong>{item.name}</strong><small>{item.game}</small></span>
                <time>{characterTab === 'birthday' ? item.date : item.date.replace('生日', '实装')}</time>
                <ArrowRight size={16} />
              </a>
            ))}
          </div>
        </section>
      </main>

      <footer className="pv3-footer">
        <a href="/" className="pv3-footer-brand"><b>GW</b><span>GAME WIKI</span></a>
        <p>游戏、资讯与日历来自线上数据；Banner 与角色动态使用本地视觉素材。</p>
        <span>发现 · 记录 · 相遇</span>
      </footer>
    </div>
  );
}
